import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AZUL, VERDE } from "../theme";
import { useInstalarApp } from "../../../shared/hooks/useInstalarApp";

/**
 * Botón "Instalar app" (celular y escritorio).
 *
 * El estado (si se puede instalar, si ya está instalada) viene de
 * useInstalarApp, que comparte con la tarjeta "Corvana en tu celular" del portal.
 *
 * Estados posibles:
 *   - Instalable  → botón "Instalar app"
 *   - Ya instalada→ chip "App instalada" (no se ofrece instalar de nuevo)
 *   - iOS         → instrucciones, porque Safari no tiene API de instalación
 */
// color/className: otros módulos (la Garita de seguridad) reutilizan el botón.
// compacto: solo el ícono (para una barra angosta de celular).
// ocultarSiInstalada: no mostrar el chip "App instalada".
export default function InstalarAppButton({
  color = AZUL, className = "agua-btn", compacto = false, ocultarSiInstalada = false,
}) {
  const { t } = useTranslation();
  const { prompt, instalada, plataforma, instalar: abrirInstalacion } = useInstalarApp();
  const [ayudaIOS, setAyudaIOS] = useState(false);

  const esIOS = plataforma === "ios";

  async function instalar() {
    if (!prompt) { setAyudaIOS(v => !v); return; }
    await abrirInstalacion();
  }

  // ── Ya instalada ──────────────────────────────────────────────────────────
  if (instalada && ocultarSiInstalada) return null;
  if (instalada) {
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 7,
        padding: "8px 14px", borderRadius: 11, fontSize: 13, fontWeight: 700,
        background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.32)",
        color: VERDE, whiteSpace: "nowrap",
      }}>
        <i className="fa-solid fa-circle-check" /> {t("agua.appInstalada")}
      </span>
    );
  }

  // Sin evento de instalación y fuera de iOS no hay nada que ofrecer: o el
  // navegador no soporta PWA, o la página no cumple los requisitos (hace falta
  // HTTPS; en localhost sí funciona).
  if (!prompt && !esIOS) return null;

  return (
    <div style={{ position: "relative" }}>
      {compacto ? (
        <button type="button" onClick={instalar} className="gar-icono-btn"
          title={t("agua.instalarApp")} aria-label={t("agua.instalarApp")}>
          <i className="fa-solid fa-download" />
        </button>
      ) : (
      <button onClick={instalar} className={className} style={{
        background: "transparent", color,
        border: `1px solid ${color}`, padding: "8px 14px", fontSize: 13,
      }}>
        <i className="fa-solid fa-arrow-down-to-bracket" /> {t("agua.instalarApp")}
      </button>
      )}

      {ayudaIOS && (
        <div style={{
          position: "absolute", right: 0, top: "calc(100% + 8px)", zIndex: 30,
          width: 260, padding: "12px 14px", borderRadius: 12,
          background: "var(--bg-card)", border: "1px solid var(--border-card)",
          boxShadow: "0 12px 32px rgba(0,0,0,.28)", fontSize: 13,
          color: "var(--text-primary)", lineHeight: 1.5,
        }}>
          <strong style={{ display: "block", marginBottom: 6 }}>{t("agua.instalarIOSTitulo")}</strong>
          {t("agua.instalarIOSPasos")}
        </div>
      )}
    </div>
  );
}
